import React, { useEffect, useState } from 'react';
import { Download, X, Share, PlusSquare, Zap } from 'lucide-react';
import { useApp } from '../context/AppContext';

export const PwaInstallPrompt = () => {
    const { view } = useApp();
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(true);
    const [showIosInstructions, setShowIosInstructions] = useState(false);

    useEffect(() => {
        const checkStandalone = () => {
            const isStandAloneMatch = window.matchMedia('(display-mode: standalone)').matches;
            const isIOSStandAlone = (window.navigator as any).standalone === true;
            return isStandAloneMatch || isIOSStandAlone;
        };

        const standalone = checkStandalone();
        setIsStandalone(standalone);

        const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        setIsIOS(ios);

        if (standalone) return;

        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setTimeout(() => {
                const dismissed = localStorage.getItem('pwa_prompt_dismissed');
                if (!dismissed) setShowPrompt(true);
            }, 3000);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        if (ios) {
            setTimeout(() => {
                const dismissed = localStorage.getItem('pwa_prompt_dismissed');
                const lastDismissed = parseInt(dismissed || '0');
                if (Date.now() - lastDismissed > 7 * 24 * 60 * 60 * 1000) {
                    setShowPrompt(true);
                }
            }, 3000);
        }

        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }, []);

    const handleInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setShowPrompt(false);
            }
            setDeferredPrompt(null);
        } else if (isIOS) {
            setShowIosInstructions(true);
            setShowPrompt(false);
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
    };

    const handleDismissIosInstructions = () => {
        setShowIosInstructions(false);
        localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
    };

    if (showIosInstructions && !isStandalone) {
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
                <div className="bg-gradient-to-b from-gray-900 to-black border border-cyan-500/30 rounded-3xl p-6 md:p-8 max-w-sm w-full shadow-[0_0_40px_rgba(34,211,238,0.2)] text-center relative overflow-hidden animate-in zoom-in-95 duration-500 delay-100">
                    
                    {/* Glowing Background Orbs & Accents */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_20px_rgba(34,211,238,0.8)]"></div>
                    <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-[radial-gradient(ellipse_at_center,rgba(34,211,238,0.15)__0%,rgba(0,0,0,0)_60%)] pointer-events-none"></div>

                    <div className="relative z-10 flex flex-col items-center">
                        <div className="w-16 h-16 bg-white/5 rounded-2xl border border-white/10 flex items-center justify-center mb-6 relative group">
                            <Zap className="text-cyan-400 shrink-0 w-8 h-8 group-hover:scale-110 transition-transform" strokeWidth={1.5}/>
                            <div className="absolute inset-0 bg-cyan-500/20 blur-xl rounded-full pointer-events-none"></div>
                        </div>

                        <h3 className="text-2xl font-black text-white mb-2 tracking-tight drop-shadow-sm">Inizializza Sistema</h3>
                        <p className="text-sm text-gray-300 mb-8 leading-relaxed px-2">
                            Il tuo dispositivo Apple richiede un'installazione manuale. Tocca <Share className="inline w-[18px] h-[18px] text-cyan-400 mx-1 align-sub"/> <b className="text-white">Condividi</b> in basso e seleziona:<br/>
                            <span className="inline-block mt-4 bg-white/5 px-4 py-2.5 rounded-xl font-mono text-cyan-300 text-xs shadow-inner border border-white/10 flex items-center gap-2 justify-center max-w-fit mx-auto">
                                <PlusSquare size={16} className="text-cyan-400" /> Aggiungi alla schermata Home
                            </span>
                        </p>

                        <button 
                            onClick={handleDismissIosInstructions}
                            className="w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-black rounded-xl shadow-[0_0_15px_rgba(8,145,178,0.5)] hover:shadow-[0_0_25px_rgba(8,145,178,0.7)] hover:scale-[1.02] active:scale-95 transition-all outline-none"
                        >
                            Avvia Sincronizzazione
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!showPrompt || isStandalone) return null;

    if (view === 'rider-locked' || view === 'rider' || window.location.pathname.startsWith('/rider')) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4">
            <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl shadow-2xl p-4 sm:p-5 flex items-start gap-4 border border-white/10 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="bg-white/20 p-3 rounded-xl shrink-0 relative z-10 backdrop-blur-sm">
                    <Download className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 relative z-10">
                    <h3 className="font-bold text-sm sm:text-base leading-tight mb-1">
                        Scarica l'App Ufficiale
                    </h3>
                    <p className="text-white/90 text-[11px] sm:text-xs mb-3 leading-snug">
                        {view === 'admin' ? "Installa l'App sul tuo dispositivo per gestire gli ordini in modo rapido e ricevere tutte le notifiche native." : view === 'rider' ? "Stai usando l'App Rider dal browser. Installa l'App ufficiale per una migliore esperienza di navigazione e notifiche push." : "Per un'esperienza fluida, nativa e pensata dallo sviluppatore, aggiungi l'Applicazione Ufficiale al tuo schermo!"}
                    </p>
                    <div className="flex gap-2">
                        <button 
                            onClick={handleInstall}
                            className="bg-white text-orange-600 hover:bg-gray-100 px-4 py-2 rounded-lg font-black text-xs sm:text-sm transition-colors shadow-sm"
                        >
                            Installa App
                        </button>
                    </div>
                </div>
                <button 
                    onClick={handleDismiss} 
                    className="p-2 -mr-2 -mt-2 text-white/70 hover:text-white transition-colors relative z-10"
                >
                    <X size={20} />
                </button>
            </div>
        </div>
    );
};
